import { getAlbum, getGalleryMedia, removeMediaFromAlbum, updateAlbum } from '@/app/services/albums/albums';
import * as Actions from '@/app/store/actions';
import { CircularProgress } from '@material-ui/core';
import { ArrowBack, CheckBox, CheckBoxOutlineBlank, Delete as DeleteIcon } from '@material-ui/icons';
import Pagination from '@material-ui/lab/Pagination';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AddToAlbumModal from './AddToAlbumModal';
import CreateAlbumModal from './CreateAlbumModal';
import Lightbox from './Lightbox';
import usePreloadImages from './usePreloadImages';
import './Gallery.css';

const AlbumDetail = () => {
	const { albumId } = useParams();
	const [album, setAlbum] = useState(null);
	const [media, setMedia] = useState([]);
	const [loading, setLoading] = useState(true);
	const [mediaLoading, setMediaLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [selectedMedia, setSelectedMedia] = useState(new Set());
	const [lightboxIndex, setLightboxIndex] = useState(null);
	const [removing, setRemoving] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const fetchAlbum = useCallback(async () => {
		try {
			setLoading(true);
			const res = await getAlbum(albumId);
			setAlbum(res.data.data || res.data);
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to load album', variant: 'error' }));
			navigate('/memories/albums');
		} finally {
			setLoading(false);
		}
	}, [albumId]);

	const fetchMedia = useCallback(async () => {
		try {
			setMediaLoading(true);
			const res = await getGalleryMedia({ album_id: albumId, page });
			const items = res.data.data || [];
			setMedia(items);
			setTotalPages(res.data?.meta?.last_page || res.data?.last_page || 1);
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to load album media', variant: 'error' }));
			setMedia([]);
		} finally {
			setMediaLoading(false);
		}
	}, [albumId, page]);

	useEffect(() => {
		fetchAlbum();
	}, [fetchAlbum]);

	useEffect(() => {
		fetchMedia();
	}, [fetchMedia]);

	usePreloadImages(media);

	const handleMediaSelect = (mediaId) => {
		setSelectedMedia((prev) => {
			const next = new Set(prev);
			if (next.has(mediaId)) {
				next.delete(mediaId);
			} else {
				next.add(mediaId);
			}
			return next;
		});
	};

	const handleSelectAll = () => {
		if (selectedMedia.size === media.length) {
			setSelectedMedia(new Set());
		} else {
			setSelectedMedia(new Set(media.map((m) => m.id)));
		}
	};

	const handleRemoveSelected = async () => {
		if (selectedMedia.size === 0) return;
		if (!window.confirm(`Remove ${selectedMedia.size} item(s) from this album?`)) return;

		setRemoving(true);
		try {
			await removeMediaFromAlbum(albumId, Array.from(selectedMedia));
			dispatch(Actions.showMessage({ message: 'Media removed from album', variant: 'success' }));
			setSelectedMedia(new Set());
			fetchAlbum();
			fetchMedia();
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to remove media', variant: 'error' }));
		} finally {
			setRemoving(false);
		}
	};

	if (loading) {
		return (
			<div className="gallery-container flex justify-center items-center py-64">
				<CircularProgress size={40} />
			</div>
		);
	}

	if (!album) return null;

	return (
		<div className="gallery-container">
			{/* Header */}
			<div className="album-detail-header">
				<button className="back-button" onClick={() => navigate('/memories/albums')} type="button">
					<ArrowBack />
				</button>
				<div className="flex-1">
					<h1 className="text-20 font-bold text-gray-900">{album.name}</h1>
					{album.description && <p className="text-14 text-gray-500">{album.description}</p>}
					<span className="text-12 text-gray-400">
						{album.media_count || 0} items
						{album.room ? ` • ${album.room.name}` : ''}
						{album.type === 'smart' && ' • Smart Album'}
					</span>
				</div>
				<div className="flex items-center gap-8">
					<button
						className="flex items-center gap-4 border border-gray-300 rounded-md px-12 py-6 text-14 text-gray-700 hover:bg-gray-50 cursor-pointer bg-white"
						onClick={handleSelectAll}
						type="button"
					>
						{selectedMedia.size === media.length && media.length > 0 ? (
							<CheckBox color="primary" style={{ fontSize: 18 }} />
						) : (
							<CheckBoxOutlineBlank style={{ fontSize: 18 }} />
						)}
						Select All
					</button>
				</div>
			</div>

			{/* Media Grid */}
			{mediaLoading ? (
				<div className="flex justify-center py-64">
					<CircularProgress size={40} />
				</div>
			) : media.length > 0 ? (
				<>
					<div className="gallery-photo-grid">
						{media.map((item, index) => (
							<div
								className={`gallery-photo-item ${selectedMedia.has(item.id) ? 'selected' : ''}`}
								key={item.id}
							>
								<img
									alt=""
									loading="lazy"
									onClick={() => setLightboxIndex(index)}
									src={item.thumb || item.link}
								/>
								<div className="photo-checkbox">
									<input
										checked={selectedMedia.has(item.id)}
										onChange={() => handleMediaSelect(item.id)}
										onClick={(e) => e.stopPropagation()}
										type="checkbox"
									/>
								</div>
								{item.type === 'video' && <div className="video-duration">Video</div>}
							</div>
						))}
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="gallery-pagination">
							<Pagination
								boundaryCount={1}
								color="primary"
								count={totalPages}
								onChange={(_, value) => setPage(value)}
								page={page}
								shape="rounded"
								siblingCount={0}
							/>
						</div>
					)}
				</>
			) : (
				<div className="text-center py-64">
					<p className="text-gray-500 text-18 mb-8">This album is empty</p>
					<p className="text-gray-400 text-14">
						Select photos from the gallery and use "Add to Album" to populate this album.
					</p>
				</div>
			)}

			{/* Selection Bar */}
			{selectedMedia.size > 0 && (
				<div className="gallery-selection-bar">
					<button onClick={() => setSelectedMedia(new Set())} type="button">
						&#x2715;
					</button>
					<span className="selection-count">{selectedMedia.size}</span>
					<span className="selection-label">Items Selected</span>
					<button
						disabled={removing}
						onClick={handleRemoveSelected}
						type="button"
					>
						{removing ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Remove from Album'}
					</button>
				</div>
			)}

			{/* Lightbox */}
			{lightboxIndex !== null && media.length > 0 && (
				<Lightbox
					initialIndex={lightboxIndex}
					media={media}
					onClose={() => setLightboxIndex(null)}
				/>
			)}
		</div>
	);
};

export default AlbumDetail;
